// Цены и итоги КП в выбранном режиме НДС — одни цифры для предпросмотра, Excel и PDF всех юрлиц
// (паспорт docs/features/kp-vat-mode.md, решения 15.10.2026).
//
//  - «С НДС» — как было: цена с НДС, «ВСЕГО с НДС 22%» = Σ цена × количество, «В том числе НДС».
//  - «НДС сверху» — как в 1С:УНФ: цена без НДС до копейки, суммы строк от неё, НДС по строкам,
//    «Итого без НДС», «НДС 22%», «Всего к оплате с НДС» (vatOnTopTotals из @crm/shared).
import { VatMode, netPriceFromGross, vatOnTopTotals } from '@crm/shared'
import { KP_NDS_LABEL, KP_TOTAL_LABEL, kpNdsOf } from './kpTerms'

export interface KpVatSource {
  /** Цена за штуку с НДС (с наценкой). */
  priceWithMarkup: number
  /** Цена без НДС до копейки — есть в КП «НДС сверху». */
  priceNet?: number
  quantity: number
}

export interface KpVatTotalRow {
  /** Подпись строки итогов, с двоеточием (как в Excel-КП). */
  label: string
  value: number
  /** Главная строка — «к оплате». */
  main: boolean
}

export interface KpVatView {
  onTop: boolean
  /** «с НДС» / «без НДС» — для подписей колонок цены и суммы. */
  priceNote: 'с НДС' | 'без НДС'
  /** Цена и сумма каждой строки к печати — в порядке items. */
  lines: Array<{ price: number; sum: number }>
  /** Строки итогов сверху вниз. */
  rows: KpVatTotalRow[]
  /** Сумма к оплате (с НДС). */
  total: number
}

/** Цена без НДС строки КП «НДС сверху»: сохранённая или из цены с НДС. */
export function kpNetPrice(it: KpVatSource): number {
  return it.priceNet ?? netPriceFromGross(it.priceWithMarkup)
}

export function kpVatView(
  items: ReadonlyArray<KpVatSource>,
  vatMode: VatMode = VatMode.INCLUDED,
): KpVatView {
  if (vatMode !== VatMode.ON_TOP) {
    const lines = items.map((it) => ({
      price: it.priceWithMarkup,
      sum: it.priceWithMarkup * it.quantity,
    }))
    const total = lines.reduce((s, l) => s + l.sum, 0)
    return {
      onTop: false,
      priceNote: 'с НДС',
      lines,
      total,
      rows: [
        { label: KP_TOTAL_LABEL, value: total, main: true },
        { label: KP_NDS_LABEL, value: kpNdsOf(total), main: false },
      ],
    }
  }
  const nets = items.map(kpNetPrice)
  const t = vatOnTopTotals(items.map((it, i) => ({ netPrice: nets[i]!, quantity: it.quantity })))
  return {
    onTop: true,
    priceNote: 'без НДС',
    lines: nets.map((price, i) => ({ price, sum: t.lines[i]!.sum })),
    total: t.total,
    rows: [
      { label: 'Итого без НДС:', value: t.net, main: false },
      { label: 'НДС 22%:', value: t.vat, main: false },
      { label: 'Всего к оплате с НДС:', value: t.total, main: true },
    ],
  }
}

/**
 * Условия поставки в режиме НДС: в «НДС сверху» первый пункт говорит, что цены без НДС.
 * Текст остальных пунктов не меняется.
 */
export function kpTermsFor(terms: readonly string[], onTop: boolean): string[] {
  if (!onTop) return [...terms]
  return terms.map((t, i) =>
    i === 0 && t.startsWith('1. Цены указаны в рублях')
      ? `${t.startsWith('1. Цены указаны в рублях РФ') ? '1. Цены указаны в рублях РФ' : '1. Цены указаны в рублях'} без НДС, НДС 22% начисляется сверху.`
      : t,
  )
}
